import { useState, useEffect, useRef } from 'react'
import { api } from '../lib/api'
import { FacilitatorGuard } from '../components/FacilitatorGuard'
import AdminNav from '../components/AdminNav'
import {
  PaperPlaneRight,
  ArrowCounterClockwise,
  Trash,
  UploadSimple,
  CheckCircle,
  WarningCircle,
  Clock,
} from '@phosphor-icons/react'

interface Invitation {
  id: string
  emailAddress: string
  status: string
  createdAt: number
  revoked?: boolean
}

const CREW_OPTIONS = ['audio', 'video', 'stage', 'it']
const TEAM_OPTIONS = ['installation', 'operations']
const STATUS_TABS = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'accepted', label: 'Accepted', icon: CheckCircle },
  { key: 'revoked', label: 'Revoked / Expired', icon: WarningCircle },
]

function InvitationRow({
  invitation,
  onRevoke,
  onResend,
}: {
  invitation: Invitation
  onRevoke: (id: string) => void
  onResend: (inv: Invitation) => void
}) {
  const [revoking, setRevoking] = useState(false)
  const [resending, setResending] = useState(false)

  async function revoke() {
    if (!confirm(`Revoke invitation for ${invitation.emailAddress}?`)) return
    setRevoking(true)
    try {
      await api.revokeInvitation(invitation.id)
      onRevoke(invitation.id)
    } catch {
      alert('Failed to revoke invitation')
      setRevoking(false)
    }
  }

  async function resend() {
    if (!confirm(`Resend invitation to ${invitation.emailAddress}?`)) return
    setResending(true)
    try {
      await api.resendInvitation(invitation.id)
      onResend(invitation)
    } catch {
      alert('Failed to resend invitation')
    } finally {
      setResending(false)
    }
  }

  const isExpired = invitation.status === 'pending' && Date.now() - invitation.createdAt > 30 * 24 * 60 * 60 * 1000

  return (
    <tr>
      <td>{invitation.emailAddress}</td>
      <td>
        <span className={`status-pill status-pill--${invitation.status}`}>
          {invitation.status}
          {isExpired && <span className="status-pill__sub">expired</span>}
        </span>
      </td>
      <td>{new Date(invitation.createdAt).toLocaleDateString()}</td>
      <td>{isExpired ? 'Yes' : 'No'}</td>
      <td className="actions-cell">
        {invitation.status === 'pending' && (
          <>
            <button
              type="button"
              onClick={resend}
              disabled={resending}
              className="btn btn-secondary btn-sm"
              title="Resend"
            >
              <ArrowCounterClockwise size={14} />
              {resending ? '…' : 'Resend'}
            </button>
            <button
              type="button"
              onClick={revoke}
              disabled={revoking}
              className="btn btn-danger btn-sm"
              title="Revoke"
            >
              <Trash size={14} />
              {revoking ? '…' : 'Revoke'}
            </button>
          </>
        )}
      </td>
    </tr>
  )
}

function InvitationForm({ onCreated }: { onCreated: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('trainee')
  const [crew, setCrew] = useState('video')
  const [team, setTeam] = useState('operations')
  const [roleSlug, setRoleSlug] = useState('td')
  const [sending, setSending] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return

    setSending(true)
    try {
      await api.createInvitation({
        emailAddress: email.trim(),
        role,
        assignments: [{ crew, team, role: roleSlug, primary: true }],
      })
      setEmail('')
      onCreated()
    } catch (err: any) {
      alert(err.message || 'Failed to send invitation')
    } finally {
      setSending(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="invitation-form">
      <div className="form-row">
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="brother@example.com"
          />
        </label>
      </div>

      <div className="form-row">
        <label>
          Role
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="trainee">Trainee</option>
            <option value="facilitator">Facilitator</option>
          </select>
        </label>
      </div>

      <div className="form-row form-row--three">
        <label>
          Crew
          <select value={crew} onChange={(e) => setCrew(e.target.value)}>
            {CREW_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <label>
          Team
          <select value={team} onChange={(e) => setTeam(e.target.value)}>
            {TEAM_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>

        <label>
          Assignment Role
          <input
            type="text"
            value={roleSlug}
            onChange={(e) => setRoleSlug(e.target.value)}
            placeholder="td, switcher, camera…"
            required
          />
        </label>
      </div>

      <button type="submit" className="btn btn-primary" disabled={sending}>
        <PaperPlaneRight size={16} />
        {sending ? 'Sending…' : 'Send Invitation'}
      </button>
    </form>
  )
}

function BulkUpload({ onUploaded }: { onUploaded: () => void }) {
  const [parsing, setParsing] = useState(false)
  const [results, setResults] = useState<Array<{ emailAddress: string; success: boolean; error?: string }> | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  function parseCSV(text: string): Array<{
    emailAddress: string
    role?: string
    crew?: string
    team?: string
    assignmentRole?: string
  }> {
    const lines = text.split('\n').filter((l) => l.trim())
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase())
    const emailIdx = headers.indexOf('email')
    const roleIdx = headers.indexOf('role')
    const crewIdx = headers.indexOf('crew')
    const teamIdx = headers.indexOf('team')
    const assignmentRoleIdx = headers.indexOf('assignment_role')

    if (emailIdx === -1) {
      throw new Error('CSV must have an "email" column')
    }

    return lines.slice(1).map((line) => {
      const cols = line.split(',').map((c) => c.trim())
      return {
        emailAddress: cols[emailIdx],
        role: roleIdx >= 0 ? cols[roleIdx] : 'trainee',
        crew: crewIdx >= 0 ? cols[crewIdx] : 'video',
        team: teamIdx >= 0 ? cols[teamIdx] : 'operations',
        assignmentRole: assignmentRoleIdx >= 0 ? cols[assignmentRoleIdx] : 'trainee',
      }
    }).filter((r) => r.emailAddress)
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setParsing(true)
    setResults(null)

    try {
      const text = await file.text()
      const rows = parseCSV(text)

      const invitations = rows.map((r) => ({
        emailAddress: r.emailAddress,
        role: r.role || 'trainee',
        assignments: [{
          crew: r.crew || 'video',
          team: r.team || 'operations',
          role: r.assignmentRole || 'trainee',
          primary: true,
        }],
      }))

      const res = await api.createBulkInvitations(invitations)
      setResults(res.results)
      onUploaded()
    } catch (err: any) {
      alert(err.message || 'Failed to parse CSV')
    } finally {
      setParsing(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="bulk-upload">
      <input
        ref={fileRef}
        type="file"
        accept=".csv"
        onChange={handleFile}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => fileRef.current?.click()}
        disabled={parsing}
      >
        <UploadSimple size={16} />
        {parsing ? 'Uploading…' : 'Bulk Upload CSV'}
      </button>
      {results && (
        <div className="bulk-upload-results">
          <p>
            {results.filter((r) => r.success).length} succeeded,{' '}
            {results.filter((r) => !r.success).length} failed
          </p>
          {results.filter((r) => !r.success).length > 0 && (
            <ul className="bulk-upload-errors">
              {results.filter((r) => !r.success).map((r, i) => (
                <li key={i}>{r.emailAddress}: {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function InvitationsList() {
  const [invitations, setInvitations] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusTab, setStatusTab] = useState('pending')

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await api.getInvitations(statusTab)
      setInvitations(data.invitations)
    } catch (err) {
      console.error('Failed to load invitations:', err)
      setError(err instanceof Error ? err.message : 'Failed to load invitations')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [statusTab])

  function handleRevoke(id: string) {
    setInvitations((prev) => prev.filter((inv) => inv.id !== id))
  }

  function handleResend(_inv: Invitation) {
    // After resend, refresh the list
    load()
  }

  return (
    <div>
      <InvitationForm onCreated={load} />

      <div className="invitations-toolbar">
        <div className="status-tabs">
          {STATUS_TABS.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.key}
                className={`status-tab ${statusTab === tab.key ? 'status-tab--active' : ''}`}
                onClick={() => setStatusTab(tab.key)}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            )
          })}
        </div>
        <BulkUpload onUploaded={load} />
      </div>

      <h2>{STATUS_TABS.find((t) => t.key === statusTab)?.label} Invitations</h2>
      {loading ? (
        <p>Loading…</p>
      ) : error ? (
        <p className="error">Error: {error}</p>
      ) : invitations.length === 0 ? (
        <p>No {statusTab} invitations.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Sent</th>
              <th>Expired</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => (
              <InvitationRow
                key={inv.id}
                invitation={inv}
                onRevoke={handleRevoke}
                onResend={handleResend}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default function AdminInvitationsPage() {
  return (
    <FacilitatorGuard>
      <div className="admin-page">
        <h1>Invitations</h1>
        <p>Send new invitations and manage pending ones.</p>
        <AdminNav />
        <InvitationsList />
      </div>
    </FacilitatorGuard>
  )
}
